/**
 * 邀请码管理组件 - 管理员管理邀请码
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl, API_ENDPOINTS } from '../../config/api';

interface InvitationCode {
  id: string;
  code: string;
  createdBy: string;
  usageLimit: number;
  usedCount: number;
  isActive: boolean;
  createdAt: number;
  expiresAt?: number | null;
  usedBy: string[];
  description?: string;
  lastUsedAt?: number;
}

interface InvitationStats {
  totalCodes: number;
  activeCodes: number;
  exhaustedCodes: number;
  totalUsages: number;
  todayRegistrations: number;
}

interface InvitationListResponse {
  invitations: InvitationCode[];
  total: number;
  page: number;
  pageSize: number;
}

interface CreateInvitationData {
  usageLimit: number;
  code?: string;
  expiresAt?: number;
  description?: string;
}

const InvitationManagement: React.FC = () => {
  const { token } = useAuth();
  
  // 处理API响应的通用函数
  const handleApiResponse = async (response: Response) => {
    if (response.status === 401) {
      const data = await response.json();
      const errorCode = data.error?.code;
      
      if (errorCode === 'ACCOUNT_DISABLED' || 
          errorCode === 'INVALID_TOKEN' || 
          errorCode === 'USER_NOT_FOUND') {
        // 清除本地存储的认证信息
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        
        // 重定向到登录页面
        window.location.href = '/auth/login';
        
        // 显示错误消息
        if (errorCode === 'ACCOUNT_DISABLED') {
          alert('您的账号已被禁用，请联系管理员');
        } else {
          alert('登录已过期，请重新登录');
        }
        
        throw new Error('认证失败');
      }
    }
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error?.message || '请求失败');
    }
    
    return await response.json();
  };

  const [invitations, setInvitations] = useState<InvitationCode[]>([]);
  const [stats, setStats] = useState<InvitationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalInvitations, setTotalInvitations] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // 创建邀请码表单状态
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormData, setCreateFormData] = useState<CreateInvitationData>({
    usageLimit: 10,
    code: '',
    description: ''
  });
  const [isCreating, setIsCreating] = useState(false);

  // 使用详情状态
  const [showUsageDetails, setShowUsageDetails] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<InvitationCode | null>(null);
  const [usageDetails, setUsageDetails] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        status: statusFilter,
      });
      
      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.ADMIN.INVITATIONS)}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await handleApiResponse(response);
      if (data.success) {
        setInvitations(data.data.invitations);
        setTotalInvitations(data.data.total);
        setError(null);
      } else {
        throw new Error(data.error?.message || '获取邀请码列表失败');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取邀请码列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN.INVITATION_STATS), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await handleApiResponse(response);
      if (data.success) {
        setStats(data.data);
      } else {
        throw new Error(data.error?.message || '获取统计信息失败');
      }
    } catch (err) {
      console.error('获取统计信息失败:', err);
    }
  };

  const createInvitation = async () => {
    try {
      setIsCreating(true);
      const requestData: CreateInvitationData = {
        usageLimit: createFormData.usageLimit,
      };
      
      if (createFormData.code?.trim()) {
        requestData.code = createFormData.code.trim();
      }
      
      if (createFormData.description?.trim()) {
        requestData.description = createFormData.description.trim();
      }

      const response = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN.INVITATIONS), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const data = await handleApiResponse(response);
      if (data.success) {
        // 重置表单
        setCreateFormData({
          usageLimit: 10,
          code: '',
          description: ''
        });
        setShowCreateForm(false);
        
        // 刷新列表和统计
        fetchInvitations();
        fetchStats();
        
        alert('邀请码创建成功！');
      } else {
        throw new Error(data.error?.message || '创建邀请码失败');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : '创建邀请码失败');
    } finally {
      setIsCreating(false);
    }
  };

  // 获取邀请码使用详情
  const fetchUsageDetails = async (invitation: InvitationCode) => {
    if (!invitation.usedBy || invitation.usedBy.length === 0) {
      setUsageDetails([]);
      setSelectedInvitation(invitation);
      setShowUsageDetails(true);
      return;
    }

    try {
      setLoadingDetails(true);
      
      // 获取使用该邀请码的用户详细信息
      const userPromises = invitation.usedBy.map(async (userId) => {
        try {
                const response = await fetch(`${buildApiUrl(API_ENDPOINTS.ADMIN.USERS)}/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
          
          const data = await handleApiResponse(response);
          if (data.success) {
            return data.data;
          }
          return null;
        } catch (err) {
          console.error(`获取用户 ${userId} 信息失败:`, err);
          return { id: userId, email: '用户信息获取失败', createdAt: null };
        }
      });

      const users = await Promise.all(userPromises);
      setUsageDetails(users.filter(user => user !== null));
      setSelectedInvitation(invitation);
      setShowUsageDetails(true);
    } catch (err) {
      alert('获取使用详情失败: ' + (err instanceof Error ? err.message : '未知错误'));
    } finally {
      setLoadingDetails(false);
    }
  };

  const toggleInvitationStatus = async (id: string, currentStatus: boolean) => {
    try {
      const action = currentStatus ? 'disable' : 'enable';
      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.ADMIN.INVITATIONS)}/${id}/${action}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await handleApiResponse(response);
      if (data.success) {
        fetchInvitations();
        fetchStats();
        alert(data.data.message);
      } else {
        throw new Error(data.error?.message || `${currentStatus ? '禁用' : '启用'}邀请码失败`);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : `${currentStatus ? '禁用' : '启用'}邀请码失败`);
    }
  };

  const deleteInvitation = async (id: string, code: string) => {
    if (!confirm(`确定要删除邀请码 ${code} 吗？此操作不可撤销。`)) {
      return;
    }

    try {
      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.ADMIN.INVITATIONS)}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await handleApiResponse(response);
      if (data.success) {
        fetchInvitations();
        fetchStats();
        alert(data.data.message);
      } else {
        throw new Error(data.error?.message || '删除邀请码失败');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : '删除邀请码失败');
    }
  };

  useEffect(() => {
    fetchInvitations();
    fetchStats();
  }, [currentPage, pageSize, statusFilter, searchTerm, token]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchInvitations();
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (invitation: InvitationCode) => {
    if (!invitation.isActive) {
      return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">已禁用</span>;
    }
    if (invitation.usedCount >= invitation.usageLimit) {
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">已用完</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">可用</span>;
  };

  const totalPages = Math.ceil(totalInvitations / pageSize);

  if (loading && invitations.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 统计信息 */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-1">总邀请码</h3>
            <p className="text-2xl font-semibold text-gray-900">{stats.totalCodes}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-1">可用邀请码</h3>
            <p className="text-2xl font-semibold text-green-600">{stats.activeCodes}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-1">已用完</h3>
            <p className="text-2xl font-semibold text-red-600">{stats.exhaustedCodes}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-1">总使用次数</h3>
            <p className="text-2xl font-semibold text-blue-600">{stats.totalUsages}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-1">今日注册</h3>
            <p className="text-2xl font-semibold text-purple-600">{stats.todayRegistrations}</p>
          </div>
        </div>
      )}

      {/* 页面头部 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">邀请码管理</h2>
            <p className="text-gray-600 mt-1">管理系统中的所有邀请码</p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            + 创建邀请码
          </button>
        </div>

        {/* 搜索和过滤 */}
        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">搜索邀请码</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="输入邀请码或描述..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">状态筛选</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">全部状态</option>
              <option value="active">可用</option>
              <option value="exhausted">已用完</option>
              <option value="expired">已过期</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              搜索
            </button>
          </div>
        </form>
      </div>

      {/* 邀请码列表 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="text-red-400 mr-3">⚠️</div>
              <div>
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && invitations.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🎫</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">暂无邀请码</h3>
            <p className="text-gray-600 mb-4">还没有创建任何邀请码</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              创建第一个邀请码
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      邀请码
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      使用情况
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      创建时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      描述
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invitations.map((invitation) => (
                    <tr key={invitation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {invitation.code}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {invitation.usedCount} / {invitation.usageLimit}
                        </div>
                        <div className="text-xs text-gray-500">
                          {invitation.lastUsedAt && (
                            `最后使用: ${formatDate(invitation.lastUsedAt)}`
                          )}
                        </div>
                        {invitation.usedCount > 0 && (
                          <button
                            onClick={() => fetchUsageDetails(invitation)}
                            disabled={loadingDetails}
                            className="text-xs text-blue-600 hover:text-blue-800 mt-1 block"
                          >
                            {loadingDetails ? '加载中...' : '查看详情'}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(invitation)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(invitation.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {invitation.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => toggleInvitationStatus(invitation.id, invitation.isActive)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            invitation.isActive
                              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        >
                          {invitation.isActive ? '禁用' : '启用'}
                        </button>
                        <button
                          onClick={() => deleteInvitation(invitation.id, invitation.code)}
                          className="px-3 py-1 bg-red-100 text-red-800 rounded text-xs font-medium hover:bg-red-200"
                        >
                          删除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  上一页
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  下一页
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    显示第 <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> 到{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, totalInvitations)}
                    </span>{' '}
                    条，共 <span className="font-medium">{totalInvitations}</span> 条邀请码
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      上一页
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      下一页
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* 创建邀请码模态框 */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">创建邀请码</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    使用次数限制 *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={createFormData.usageLimit}
                    onChange={(e) => setCreateFormData(prev => ({
                      ...prev,
                      usageLimit: parseInt(e.target.value) || 1
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1-1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    自定义邀请码（可选）
                  </label>
                  <input
                    type="text"
                    value={createFormData.code}
                    onChange={(e) => setCreateFormData(prev => ({
                      ...prev,
                      code: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="留空自动生成"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    3-32个字符，支持字母、数字、下划线、连字符
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    描述（可选）
                  </label>
                  <textarea
                    value={createFormData.description}
                    onChange={(e) => setCreateFormData(prev => ({
                      ...prev,
                      description: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="邀请码用途描述"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={isCreating}
                >
                  取消
                </button>
                <button
                  onClick={createInvitation}
                  disabled={isCreating}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                >
                  {isCreating ? '创建中...' : '创建'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 使用详情模态框 */}
      {showUsageDetails && selectedInvitation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                邀请码使用详情 - {selectedInvitation.code}
              </h3>
              
              <div className="mb-4 p-4 bg-gray-50 rounded-md">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">使用次数：</span>
                    <span className="font-medium">{selectedInvitation.usedCount} / {selectedInvitation.usageLimit}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">创建时间：</span>
                    <span className="font-medium">{formatDate(selectedInvitation.createdAt)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">最后使用：</span>
                    <span className="font-medium">
                      {selectedInvitation.lastUsedAt ? formatDate(selectedInvitation.lastUsedAt) : '未使用'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">状态：</span>
                    <span className="font-medium">
                      {selectedInvitation.isActive ? '激活' : '已禁用'}
                    </span>
                  </div>
                </div>
                {selectedInvitation.description && (
                  <div className="mt-2">
                    <span className="text-gray-500">描述：</span>
                    <span className="font-medium">{selectedInvitation.description}</span>
                  </div>
                )}
              </div>

              {loadingDetails ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <p className="mt-2 text-gray-500">加载用户信息中...</p>
                </div>
              ) : usageDetails.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>此邀请码尚未被使用</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          用户邮箱
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          注册时间
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          账号状态
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          邮箱验证
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          用户角色
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {usageDetails.map((user, index) => (
                        <tr key={user.id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {user.email}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.createdAt ? formatDate(user.createdAt) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.isActive === false 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {user.isActive === false ? '已禁用' : '正常'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.emailVerified 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {user.emailVerified ? '已验证' : '未验证'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.role === 'admin' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {user.role === 'admin' ? '管理员' : '普通用户'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setShowUsageDetails(false);
                    setSelectedInvitation(null);
                    setUsageDetails([]);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationManagement; 